/**
 * Band-set invariants (P2, 2026-08-31) — the pure, node-testable rule that
 * decides whether a set of price bands is a legal COVER.
 *
 * WHY THIS IS A MODULE AND NOT A COMPONENT CHECK. The repo's test environment
 * is plain `node` with no DOM (vitest.config.ts:24), so a rule that lives
 * inside a `.tsx` cannot be reached by a guard at all — and a guard that
 * re-declares the rule in the test file asserts the wrong function
 * (`erp-web-gate-reality` §2, the 2026-08-28 precedent where reinstating the
 * defect left six new tests green). The rule therefore lives HERE, dependency
 * -free (types only), and BOTH the store reducers and the Price Setup band
 * editor reach the same body.
 *
 * THE SEALED RULE, in one sentence: within one (band set × currency)
 * partition the cuts are HALF-OPEN `[minInclusive, maxExclusive)`, GAPLESS
 * and NON-OVERLAPPING, a value exactly on a boundary belongs to the HIGHER
 * band, and AT MOST ONE band — the top one — may be open-ended
 * (`maxExclusive: null`).
 *
 * A save that would leave a gap or an overlap is REFUSED and NAMES the
 * conflicting boundary (`BandSetViolation` carries both neighbours and both
 * numbers). It is never silently repaired: absorbing a deactivated band's
 * range into its neighbour would re-cut a bound the operator did not choose,
 * which is the same "prices move through a lookup instead of through a
 * decision" failure RULING R-BAND rejects.
 *
 * WHAT THIS FILE DOES NOT DECIDE, stated so it is not read as settled:
 * - It does not require a set's floor to be 0. Below the lowest cut there is
 *   simply no band, which is the pre-existing `'no_band_match'` refusal —
 *   loud, not silent. Constraining the floor would be inventing policy.
 * - It does not forbid two bands carrying the same `group` letter in one set.
 *   The markup-rule key is `band.id|source|level`, so duplicate letters
 *   resolve correctly; whether they are DESIRABLE is a naming decision that
 *   belongs to the owner, not to a guard.
 * - It does not forbid a set whose bands are ALL inactive. That is a legal
 *   in-progress state while an operator rebuilds a ladder; the consequence
 *   (the group's products stop resolving a level price) is visible in the
 *   resolver's existing refusal, not hidden.
 *
 * R-BAND and R-AD are both untouched here: this file says nothing about WHAT
 * quantity is banded on (still `breakdown.pricingBase`, always) and it
 * retires or re-cuts no seeded band's values.
 */

import type { PriceGroupBand, PriceGroupCode } from '@/types/api/pricing';

/**
 * The synthetic scope key for the DEFAULT (unscoped) band set — the rows that
 * carry no `productGroupId`. A synthetic key, never a stored value: nothing is
 * ever written to a band with this string.
 */
export const DEFAULT_BAND_SET_SCOPE = '__default__';

/**
 * ACTIVE is the ABSENT-DEFAULT. `isActive` is optional on `PriceGroupBand` for
 * the same reason `productGroupId` is (see its docblock): every seeded row
 * would otherwise change shape, moving the seed hash and breaking the pinned
 * `toEqual` band assertions for a field that adds no information. Absent ⇒
 * active ⇒ byte-identical to the pre-CRUD engine.
 */
export function isBandActive(band: PriceGroupBand): boolean {
  return band.isActive !== false;
}

/** Which band SET a row belongs to — its owning product group, or the default. */
export function bandSetScopeOf(band: PriceGroupBand): string {
  return band.productGroupId ?? DEFAULT_BAND_SET_SCOPE;
}

/**
 * The ACTIVE rows of ONE (scope × currency) partition, ascending by lower
 * bound. This is the exact population `validateBandSet` adjudicates, and the
 * exact population `resolvePriceGroup` can match against — one definition, so
 * "the editor said it was legal" and "the resolver found a band" cannot drift.
 *
 * Ties on `minInclusive` are ordered by upper bound (open-ended last) so a
 * duplicate lower bound always surfaces as an OVERLAP against its neighbour
 * rather than as a direction-dependent verdict.
 */
export function activeBandsInSet(
  bands: readonly PriceGroupBand[],
  scope: string,
  currency: string
): PriceGroupBand[] {
  return bands
    .filter(
      (band) =>
        isBandActive(band) && bandSetScopeOf(band) === scope && band.currency === currency
    )
    .sort((a, b) => {
      if (a.minInclusive !== b.minInclusive) return a.minInclusive - b.minInclusive;
      if (a.maxExclusive === null) return 1;
      if (b.maxExclusive === null) return -1;
      return a.maxExclusive - b.maxExclusive;
    });
}

export type BandSetViolationCode =
  /** `maxExclusive` is not strictly above `minInclusive` — a band covering nothing. */
  | 'BAND_RANGE_INVALID'
  /** An open-ended band sits below another band (or two are open-ended). */
  | 'BAND_OPEN_END_NOT_TOP'
  /** Two consecutive cuts overlap — one value would fall in two bands. */
  | 'BAND_OVERLAP'
  /** Two consecutive cuts leave a hole — a value in it falls in no band. */
  | 'BAND_GAP';

/**
 * WHICH boundary conflicts — the payload the refusal message is built from, so
 * the operator reads "Group B's upper bound 60 and Group C's lower bound 70
 * leave a gap" rather than "invalid". `lower`/`upper` are the two neighbours
 * involved; for a single-band fault (`BAND_RANGE_INVALID`,
 * `BAND_OPEN_END_NOT_TOP`) only the `lower` side is populated.
 */
export interface BandSetViolation {
  code: BandSetViolationCode;
  lowerBandId: string | null;
  lowerGroup: PriceGroupCode | null;
  upperBandId: string | null;
  upperGroup: PriceGroupCode | null;
  /** The lower band's `maxExclusive` (its `minInclusive` for a range fault). */
  at: number | null;
  /** The upper band's `minInclusive` (its `maxExclusive` for a range fault). */
  next: number | null;
}

/**
 * Adjudicate ONE partition's active rows (as produced by `activeBandsInSet`).
 * Returns the FIRST violation found, or `null` when the cover is legal.
 *
 * Order of checks is deliberate: a degenerate range and a misplaced open end
 * both make the pair walk report a misleading gap/overlap, so they are named
 * first and on their own terms.
 *
 * An empty set and a single-band set are both legal — there is no pair to
 * violate, and neither shape is a decision this function is entitled to make.
 */
export function validateBandSet(bands: readonly PriceGroupBand[]): BandSetViolation | null {
  const sorted = [...bands].sort((a, b) => {
    if (a.minInclusive !== b.minInclusive) return a.minInclusive - b.minInclusive;
    if (a.maxExclusive === null) return 1;
    if (b.maxExclusive === null) return -1;
    return a.maxExclusive - b.maxExclusive;
  });

  for (const band of sorted) {
    if (band.maxExclusive !== null && band.maxExclusive <= band.minInclusive) {
      return {
        code: 'BAND_RANGE_INVALID',
        lowerBandId: band.id,
        lowerGroup: band.group,
        upperBandId: null,
        upperGroup: null,
        at: band.minInclusive,
        next: band.maxExclusive,
      };
    }
  }

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const band = sorted[index];
    if (band.maxExclusive === null) {
      return {
        code: 'BAND_OPEN_END_NOT_TOP',
        lowerBandId: band.id,
        lowerGroup: band.group,
        upperBandId: sorted[index + 1].id,
        upperGroup: sorted[index + 1].group,
        at: band.minInclusive,
        next: sorted[index + 1].minInclusive,
      };
    }
  }

  for (let index = 0; index < sorted.length - 1; index += 1) {
    const lower = sorted[index];
    const upper = sorted[index + 1];
    // `lower.maxExclusive` is non-null here: the open-end walk above returned
    // on every null that is not the last element.
    const boundary = lower.maxExclusive as number;
    if (boundary === upper.minInclusive) continue;
    return {
      code: boundary > upper.minInclusive ? 'BAND_OVERLAP' : 'BAND_GAP',
      lowerBandId: lower.id,
      lowerGroup: lower.group,
      upperBandId: upper.id,
      upperGroup: upper.group,
      at: boundary,
      next: upper.minInclusive,
    };
  }

  return null;
}

/**
 * The partition a change lands in, validated end to end: filter the WHOLE
 * (already-projected) band collection down to the affected partition's active
 * rows and adjudicate them. Callers pass the collection as it WOULD be after
 * the write, so a refusal happens before anything is stored.
 */
export function validateBandSetAfterChange(
  nextBands: readonly PriceGroupBand[],
  scope: string,
  currency: string
): BandSetViolation | null {
  return validateBandSet(activeBandsInSet(nextBands, scope, currency));
}

// ---------------------------------------------------------------------------
// P3 (2026-08-31) — MOVING AN INTERIOR CUT.
//
// THE MEASURED BREAK THIS CLOSES. A gapless cover has no legal one-band edit
// that moves an interior boundary: shrinking the lower band first leaves a
// GAP, widening the upper band first leaves an OVERLAP, and `validateBandSet`
// refuses BOTH orderings. Measured on the seeded scoped set
// (A[0,25) B[25,60) C[60,140) D[140,∞) AUD, group `cat-ele-tl`): there is NO
// sequence of `updatePriceGroupBand` calls that moves the B/C cut from 60 to
// 45, so a product could never be re-banded from Price Setup at all — which
// is the round trip P3 exists to prove.
//
// WHAT THIS IS NOT. It is NOT the silent repair the P2 docblock refuses. A
// repair invents a bound nobody chose; this plans the ONE bound that is
// already implied by "the cuts are gapless" — a shared boundary is ONE number
// written on two rows, so moving it is one decision, and the surface NAMES
// the neighbour and both numbers before the operator commits. Nothing is
// absorbed, no range is widened past a neighbour's far bound (the invariant
// still adjudicates the result and still refuses), and a deactivation still
// refuses rather than planning anything.
// ---------------------------------------------------------------------------

/** The editable cuts of one band — the same members as the store's patch. */
export interface BandCutPatch {
  minInclusive?: number;
  maxExclusive?: number | null;
}

/**
 * ONE neighbour bound that must follow the edited band's bound, because the
 * two rows carry the same shared boundary number.
 */
export interface BandCutMove {
  bandId: string;
  group: PriceGroupCode;
  /** Which bound of the NEIGHBOUR moves. */
  field: 'minInclusive' | 'maxExclusive';
  from: number;
  to: number;
}

/**
 * Plan the neighbour bounds that a cut edit drags with it.
 *
 * Returns `[]` when the edit touches no shared boundary — in which case the
 * strict `updatePriceGroupBand` path is already sufficient and nothing extra
 * should be offered. Only ACTIVE rows of the SAME (set × currency) partition
 * are candidates: an inactive cut is not part of the cover, and a row in
 * another set or currency shares no boundary with this one (D27).
 *
 * An edit that makes an interior band open-ended (`maxExclusive: null` with a
 * band above it) plans NOTHING: there is no number for the upper neighbour's
 * lower bound to become, so that stays the `BAND_OPEN_END_NOT_TOP` refusal it
 * always was.
 */
export function planBandCutMoves(
  bands: readonly PriceGroupBand[],
  bandId: string,
  patch: BandCutPatch
): BandCutMove[] {
  const band = bands.find((candidate) => candidate.id === bandId);
  if (!band) return [];
  const partition = activeBandsInSet(bands, bandSetScopeOf(band), band.currency).filter(
    (candidate) => candidate.id !== bandId
  );
  const moves: BandCutMove[] = [];

  const nextMin = patch.minInclusive;
  if (nextMin !== undefined && nextMin !== band.minInclusive) {
    const lower = partition.find((candidate) => candidate.maxExclusive === band.minInclusive);
    if (lower) {
      moves.push({
        bandId: lower.id,
        group: lower.group,
        field: 'maxExclusive',
        from: band.minInclusive,
        to: nextMin,
      });
    }
  }

  const nextMax = patch.maxExclusive;
  if (nextMax !== undefined && nextMax !== band.maxExclusive && nextMax !== null) {
    const upper = partition.find(
      (candidate) => band.maxExclusive !== null && candidate.minInclusive === band.maxExclusive
    );
    if (upper) {
      moves.push({
        bandId: upper.id,
        group: upper.group,
        field: 'minInclusive',
        from: upper.minInclusive,
        to: nextMax,
      });
    }
  }

  return moves;
}

/**
 * Apply a planned move set to a band collection. Pure; array order preserved,
 * so an empty plan returns the input element-for-element.
 */
export function applyBandCutMoves(
  bands: readonly PriceGroupBand[],
  moves: readonly BandCutMove[]
): PriceGroupBand[] {
  if (moves.length === 0) return [...bands];
  return bands.map((band) => {
    const move = moves.find((candidate) => candidate.bandId === band.id);
    if (!move) return band;
    return move.field === 'minInclusive'
      ? { ...band, minInclusive: move.to }
      : { ...band, maxExclusive: move.to };
  });
}
